import Dexie, { Table } from 'dexie';
import {
  ISynchronizationItemMeta,
  APPLICABLE_STATE_NAME,
  STARRED_ACTION_TYPE,
  ISynchronizationItem,
  IHomePageInfo,
  IPdfFileMeta,
  ISetting,
  IPdfFile,
} from './dexieDB.models';

export default class NoddSynchronizationDB extends Dexie {
  synchronizationItemsMeta!: Table<ISynchronizationItemMeta, string>;
  synchronizationItems!: Table<ISynchronizationItem, string>;
  documentPdfFilesMeta!: Table<IPdfFileMeta, string>;
  homePageInfo!: Table<IHomePageInfo, string>;
  documentPdfFiles!: Table<IPdfFile, string>;
  settings!: Table<ISetting, string>;
  private static VERSION_6_O = 6;

  constructor() {
    super('NODD');
    this.version(NoddSynchronizationDB.VERSION_6_O).stores({
      synchronizationItemsMeta: '&id',
      synchronizationItems: '&id',
      documentPdfFilesMeta: '&id',
      documentPdfFiles: '&id',
      homePageInfo: '++',
      settings: '&key',
    });
  }
}

export const db = new NoddSynchronizationDB();

export async function addHomePageInfoToDB(homePageInfo: IHomePageInfo) {
  try {
    return db.homePageInfo.add(homePageInfo);
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function getAllHomePageInfo() {
  try {
    return db.homePageInfo.toArray();
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function deleteAllHomePageInfo() {
  try {
    return db.homePageInfo.clear();
  } catch (error) {
    console.error('Error... ' + error);
  }
}

export async function addSynchronizationItemsMetaToDB(documents: ISynchronizationItemMeta[]) {
  try {
    return db.synchronizationItemsMeta.bulkAdd(documents, { allKeys: true });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function deleteAllSynchronizationItemsMeta() {
  try {
    return db.synchronizationItemsMeta.clear();
  } catch (error) {
    console.error('Error... ' + error);
  }
}

export async function getAllSynchronizationItemsMeta() {
  try {
    return db.transaction('r', db.synchronizationItemsMeta, () => {
      return db.synchronizationItemsMeta.toArray();
    });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function getAllApplicableItems() {
  try {
    return db.transaction('r', db.synchronizationItemsMeta, () => {
      return db.synchronizationItemsMeta.toArray();
    });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}


export async function getSynchronizationItemMetaById(synchronizationItemMetaId: string) {
  try {
    return db.synchronizationItemsMeta.get(synchronizationItemMetaId);
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

type PdfDocumentToAddOrRemoveFromFavorites = {
  documentVersionType: APPLICABLE_STATE_NAME;
  starredActionType: STARRED_ACTION_TYPE
  synchronizationId: string;
  isAckRequired: boolean;
};

export async function updateSynchronizationItemMetaIsAckRequiredFavoritePdfDocuments({synchronizationId, documentVersionType, isAckRequired, starredActionType}: PdfDocumentToAddOrRemoveFromFavorites) {
  try {
    db.transaction('rw', db.synchronizationItemsMeta, async () => {
      const synchronizationItemMeta = await db.synchronizationItemsMeta.get(synchronizationId);

      if (!synchronizationItemMeta) {
        return 0;
      }

      let starredApplicableDocumentsCount = synchronizationItemMeta?.starredApplicableDocumentsCount ?? 0;
      let starredNextApplicableDocumentsCount = synchronizationItemMeta?.starredNextApplicableDocumentsCount ?? 0;
      let starredIsAckRequiredApplicableDocumentsCount = synchronizationItemMeta?.starredIsAckRequiredApplicableDocumentsCount ?? 0;
      let starredIsAckRequiredNextApplicableDocumentsCount = synchronizationItemMeta?.starredIsAckRequiredNextApplicableDocumentsCount ?? 0;
      
      if (documentVersionType === APPLICABLE_STATE_NAME.APPLICABLE) {
        if(starredActionType === STARRED_ACTION_TYPE.ADD && isAckRequired) {
          starredIsAckRequiredApplicableDocumentsCount = starredIsAckRequiredApplicableDocumentsCount + 1
          starredApplicableDocumentsCount = starredApplicableDocumentsCount + 1;
        } else if(starredActionType === STARRED_ACTION_TYPE.ADD && !isAckRequired) {
          starredApplicableDocumentsCount = starredApplicableDocumentsCount + 1;
        } else if(starredActionType === STARRED_ACTION_TYPE.DELETE && isAckRequired) {
          starredIsAckRequiredApplicableDocumentsCount = starredIsAckRequiredApplicableDocumentsCount === 0 ? 0 : starredIsAckRequiredApplicableDocumentsCount - 1;
          starredApplicableDocumentsCount = starredApplicableDocumentsCount === 0 ? 0 : starredApplicableDocumentsCount - 1;
        } else {
          starredApplicableDocumentsCount = starredApplicableDocumentsCount === 0 ? 0 : starredApplicableDocumentsCount - 1;
        }
      } else {
        if(starredActionType === STARRED_ACTION_TYPE.ADD && isAckRequired) {
          starredIsAckRequiredNextApplicableDocumentsCount = starredIsAckRequiredNextApplicableDocumentsCount + 1
          starredNextApplicableDocumentsCount = starredNextApplicableDocumentsCount + 1;
        } else if(starredActionType === STARRED_ACTION_TYPE.ADD && !isAckRequired) {
          starredNextApplicableDocumentsCount = starredNextApplicableDocumentsCount + 1;
        } else if(starredActionType === STARRED_ACTION_TYPE.DELETE && isAckRequired) {
          starredIsAckRequiredNextApplicableDocumentsCount = starredIsAckRequiredNextApplicableDocumentsCount === 0 ? 0 : starredIsAckRequiredNextApplicableDocumentsCount - 1;
          starredNextApplicableDocumentsCount = starredNextApplicableDocumentsCount === 0 ? 0 : starredNextApplicableDocumentsCount - 1;
        } else {
          starredNextApplicableDocumentsCount = starredNextApplicableDocumentsCount === 0 ? 0 : starredNextApplicableDocumentsCount - 1;
        }
      }

      let newData = {
        starredApplicableDocumentsCount,
        starredNextApplicableDocumentsCount,
        starredIsAckRequiredApplicableDocumentsCount,
        starredIsAckRequiredNextApplicableDocumentsCount,
      } as ISynchronizationItemMeta;

      console.log(synchronizationItemMeta.starredDocumentVersionsCount, newData);
      return db.synchronizationItemsMeta.update(synchronizationId, newData);
    });
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function updateDocumentFavBoolean( synchronizationId: string, documentVersionType: string , fileId: string , isFav: any ){
  try {
    const synchronizationItem = await db.synchronizationItems.get(synchronizationId);
    const versionDoc = documentVersionType === "APPLICABLE" ? synchronizationItem!.applicables : synchronizationItem!.nextApplicables
  
    if (synchronizationItem && versionDoc) {
      const applicableItemToUpdate = versionDoc.find((item) => item.file.id === fileId);
  
      if (applicableItemToUpdate) {
        applicableItemToUpdate.uds.isFav = isFav;
  
        await db.synchronizationItems.put(synchronizationItem);
  
        console.log("La valeur de isFav a été mise à jour avec succès.");
      } else {
        console.error("L'élément avec l'ID spécifié n'a pas été trouvé dans le tableau 'applicable'.");
      }
    } else {
      console.error("La synchronisation ou le tableau 'applicable' est introuvable.");
    }
  } catch (error) {
    console.error("Une erreur s'est produite lors de la mise à jour de la valeur de isFav :", error);
  }
}

export async function addSynchronizationItemsToDB(documents: ISynchronizationItem[]) {
  try {
    return db.synchronizationItems.bulkAdd(documents, { allKeys: true });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function getAllSynchronizationItems() {
  try {
    return db.transaction('r', db.synchronizationItems, () => {
      return db.synchronizationItems.toArray();
    });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function deleteAllSynchronizationItems() {
  try {
    return db.synchronizationItems.clear();
  } catch (error) {
    console.error('Error... ' + error);
  }
}

export async function getFirstSynchronizationItem() {
  try {
    return db.synchronizationItems.toCollection().first();
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function getSynchronizationItemById(categoryId: string) {
  try {
    return db.synchronizationItems.get(categoryId);
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function updateSynchronizationItemsToDb(newSynchronizationItems: ISynchronizationItem[]) {
  try {
    return db.synchronizationItems.bulkPut(newSynchronizationItems, { allKeys: true });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function putPdfDocument(newPdfDocument: IPdfFile) {
  try {
    return db.documentPdfFiles.put(newPdfDocument);
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function updatePdfDocumentBuffer(pdfDocumentId: string, pdfBuffer: ArrayBuffer) {
  try {
    return db.documentPdfFiles.update(pdfDocumentId, { pdfBuffer });
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function updateDocumentPdfFilesMeta(documentPdfFilesMeta: IPdfFileMeta[]) {
  try {
    return db.documentPdfFilesMeta.bulkPut(documentPdfFilesMeta, { allKeys: true });
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function updatePdfDocumentReadDate(pdfDocumentId: string, readDate: string) {
  try {
    return db.documentPdfFilesMeta.update(pdfDocumentId, { readDate });
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function updatePdfDocumentAckDate(pdfDocumentId: string, ackDate: string) {
  try {
    return db.documentPdfFilesMeta.update(pdfDocumentId, { ackDate });
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function updateDocumentPdfFilesMetaFavorites(pdfDocumentId: string, isFav: boolean) {
  try {
    return db.documentPdfFilesMeta.update(pdfDocumentId, { isFav });
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function getPdfDocumentMetaById(id: string) {
  try {
    return db.transaction('r?', db.documentPdfFilesMeta, () => {
      return db.documentPdfFilesMeta.get(id);
    });
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function getPdfDocumentMetaByIds(ids: string[]) {
  try {
    return db.transaction('r?', db.documentPdfFilesMeta, () => {
      return db.documentPdfFilesMeta.bulkGet(ids);
    });
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function getAllPdfDocumentsMeta() {
  try {
    return db.documentPdfFilesMeta.toArray();
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function getCountPdfDocumentsMeta() {
  try {
    return db.documentPdfFilesMeta.count();
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function putPdfDocumentMeta(newPdfDocumentMeta: IPdfFileMeta) {
  try {
      return await db.documentPdfFilesMeta.put(newPdfDocumentMeta);
  } catch (error) {
      console.error('Error while putting PDF document meta:', error);
      throw new Error('Failed to update PDF document meta');
  }
}

export async function updatePdfDocumentMetaThumbnail(pdfDocumentId: string, thumbnailBuffer: ArrayBuffer) {
  try {
    return db.documentPdfFilesMeta.update(pdfDocumentId, { thumbnailBuffer });
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function deletePdfDocumentsMeta(pdfDocumentsMetaToRemove: string[]) {
  try {
    return db.documentPdfFilesMeta.bulkDelete(pdfDocumentsMetaToRemove);
  } catch (error) {
    console.error('Error... ' + error);
  }
}

export async function getPdfDocumentById(id: string) {
  try {
    return db.transaction('r?', db.documentPdfFiles, () => {
      return db.documentPdfFiles.get(id);
    });
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function getPdfDocumentsByBulkIds(ids: string[]) {
  try {
    return db.documentPdfFiles.bulkGet(ids);
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function getAllPdfDocuments() {
  try {
    return db.documentPdfFiles.toArray();
  } catch (error) {
    console.error('Error... ' + error);
    return [];
  }
}

export async function deletePdfDocuments(pdfDocumentToRemove: string[]) {
  try {
    return db.documentPdfFiles.bulkDelete(pdfDocumentToRemove);
  } catch (error) {
    console.error('Error... ' + error);
  }
}
export async function getSettingByKey(settingKey: string) {
  try {
    return db.settings.get(settingKey);
  } catch (error) {
    console.error('Error... ' + error);
    return undefined;
  }
}

export async function updateSettings(setting: ISetting) {
  try {
    return db.settings.put(setting);
  } catch (error) {
    console.error('Error... ' + error);
    return 0;
  }
}

export async function resetDatabase() {
  const tables = [
    db.synchronizationItemsMeta,
    db.synchronizationItems,
    db.documentPdfFilesMeta,
    db.documentPdfFiles,
    db.homePageInfo,
    db.settings,
  ];

  return db.transaction('rw', tables, async () => {
    return Promise.all(db.tables.map((table) => table.clear()));
  });
}

// }

// export default new DexieDBManager()
